import React, { useState } from 'react'
import { Card, Button, Table, Form, Input, Select, Space, Typography, Tag } from 'antd'
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input

const LifestyleRecommendations = ({ lifestyle = [], setLifestyle }) => {
  const [editMode, setEditMode] = useState(false)
  const [currentRecommendation, setCurrentRecommendation] = useState({
    type: 'Diet',
    description: '',
    priority: 'Medium',
    additionalInfo: ''
  })
  const [form] = Form.useForm()

  const handleAddRecommendation = () => {
    form.validateFields().then((values) => {
      const newRecommendation = {
        ...values,
        id: Date.now().toString() // Generate a unique ID
      }

      if (editMode) {
        const updatedRecommendations = lifestyle.map((item) =>
          item.id === currentRecommendation.id ? newRecommendation : item
        )
        setLifestyle(updatedRecommendations)
        setEditMode(false)
      } else {
        setLifestyle([...lifestyle, newRecommendation])
      }

      // Reset form
      form.resetFields()
      setCurrentRecommendation({
        type: 'Diet',
        description: '',
        priority: 'Medium',
        additionalInfo: ''
      })
    })
  }

  const handleEdit = (record) => {
    setEditMode(true)
    setCurrentRecommendation(record)
    form.setFieldsValue(record)
  }

  const handleDelete = (id) => {
    setLifestyle(lifestyle.filter((item) => item.id !== id))
  }

  const handleCancel = () => {
    setEditMode(false)
    form.resetFields()
    setCurrentRecommendation({
      type: 'Diet',
      description: '',
      priority: 'Medium',
      additionalInfo: ''
    })
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'red'
      case 'Medium':
        return 'orange'
      case 'Low':
        return 'green'
      default:
        return 'blue'
    }
  }

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => <Tag color={getPriorityColor(priority)}>{priority}</Tag>
    },
    {
      title: 'Additional Information',
      dataIndex: 'additionalInfo',
      key: 'additionalInfo',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type='link' icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type='link' danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      )
    }
  ]

  const recommendationTypes = [
    'Diet',
    'Exercise',
    'Sleep',
    'Stress Management',
    'Substance Use',
    'Social Activity',
    'Mental Health',
    'Other'
  ]

  return (
    <div className='lifestyle-section'>
      <Card title={<Title level={4}>Lifestyle & Wellness Recommendations</Title>}>
        <Text type='secondary' style={{ display: 'block', marginBottom: 16 }}>
          Add lifestyle and wellness recommendations to improve patient health outcomes
        </Text>

        <Form
          form={form}
          layout='vertical'
          className='form-section'
          initialValues={{
            type: 'Diet',
            priority: 'Medium'
          }}
        >
          <Form.Item
            name='type'
            label='Recommendation Type'
            rules={[{ required: true, message: 'Please select a recommendation type' }]}
          >
            <Select>
              {recommendationTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='description'
            label='Description'
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <TextArea rows={3} placeholder='Enter recommendation details' />
          </Form.Item>

          <Form.Item name='priority' label='Priority Level'>
            <Select>
              <Option value='High'>High</Option>
              <Option value='Medium'>Medium</Option>
              <Option value='Low'>Low</Option>
            </Select>
          </Form.Item>

          <Form.Item name='additionalInfo' label='Additional Information'>
            <TextArea rows={2} placeholder='Any additional information or resources' />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type='primary' icon={<PlusOutlined />} onClick={handleAddRecommendation}>
                {editMode ? 'Update Recommendation' : 'Add Recommendation'}
              </Button>
              {editMode && <Button onClick={handleCancel}>Cancel</Button>}
            </Space>
          </Form.Item>
        </Form>

        <Table dataSource={lifestyle} columns={columns} rowKey='id' pagination={false} className='tracker-table' />
      </Card>
    </div>
  )
}

export default LifestyleRecommendations
